import { createContext, useContext, useState, useRef } from "react";

const MapLegendContext = createContext();

export function useMapLegendContext() {
  return useContext(MapLegendContext);
}

export const MapLegendConsumer = MapLegendContext.Consumer;

export function MapLegendProvider({ children }) {
  const [basicLegend, setBasicLegend] = useState(null);
  const [ambient, setAmbient] = useState(null);
  const [comparisonLegends, setComparisonLegendsState] = useState(null);
  const [enhanced, setEnhanced] = useState({});
  const [comparisonEnhanced, setComparisonEnhanced] = useState({});
  const [activeLand, setActiveLand] = useState(null);
  const savedLegends = useRef({});

  const handleEnhanced = (land_id) => {
    setEnhanced((prev) => {
      return { ...prev, [land_id]: !prev[land_id] };
    });
  };

  const disableEnhanced = (land_id) => {
    if (!land_id) {
      return;
    }

    setEnhanced((prev) => {
      if (!prev[land_id]) {
        return prev;
      }

      return { ...prev, [land_id]: false };
    });
  };

  const handleComparisonEnhanced = (land_id) => {
    setComparisonEnhanced((prev) => {
      return { ...prev, [land_id]: !prev[land_id] };
    });
  };

  const disableComparisonEnhanced = (land_id) => {
    if (!land_id) {
      return;
    }

    setComparisonEnhanced((prev) => {
      if (!prev[land_id]) {
        return prev;
      }

      return { ...prev, [land_id]: false };
    });
  };

  const changeBasicLegend = (land_id, legend) => {
    savedLegends.current[land_id] = {
      ...(savedLegends.current[land_id] || {}),
      basic: legend,
    };

    setAmbient(null);
    setBasicLegend(legend);
    setActiveLand(land_id);
  };

  const recoverIfExists = (land_id) => {
    if (savedLegends.current[land_id]) {
      if (savedLegends.current[land_id]["basic"]) {
        changeBasicLegend(land_id, savedLegends.current[land_id]["basic"]);
      } else if (savedLegends.current[land_id]["ambient"]) {
        handleAmbient(land_id, savedLegends.current[land_id]["ambient"])
      }
    }
  };

  const recoverBasicLegend = (land_id) => {
    const basic = savedLegends.current[land_id]?.basic;
    if (!basic) {
      return false;
    }

    changeBasicLegend(land_id, basic);
    return true;
  };

  const deleteLegend = (land_id) => {
    if (savedLegends.current[land_id]) {
      delete savedLegends.current[land_id];
    }

    if (activeLand == land_id) {
      setAmbient(null);
      setBasicLegend(null);
      setComparisonLegendsState(null);
      setActiveLand(null);
    }
  };

  const hideLegend = (land_id) => {
    if (activeLand == land_id) {
      setAmbient(null);
      setBasicLegend(null);
      setComparisonLegendsState(null);
      setActiveLand(null);
    }
  };

  const hideAmbientLegend = (land_id) => {
    if (activeLand !== land_id || ambient === null) {
      return;
    }

    setAmbient(null);
    if (basicLegend == null && comparisonLegends == null) {
      setActiveLand(null);
    }
  };

  const clearBasicLegend = (land_id) => {
    if (savedLegends.current[land_id]?.basic) {
      delete savedLegends.current[land_id].basic;
    }

    if (activeLand == land_id && basicLegend != null && ambient == null) {
      setBasicLegend(null);
      setActiveLand(null);
    }
  };

  const clearVisibleLegends = () => {
    setAmbient(null);
    setBasicLegend(null);
    setComparisonLegendsState(null);
    setActiveLand(null);
  };

  const deleteAll = () => {
    savedLegends.current = {};
    setEnhanced({});
    setComparisonEnhanced({});
    clearVisibleLegends();
  };

  const handleAmbient = (land_id, ambient) => {
    savedLegends.current[land_id] = {
      ...(savedLegends.current[land_id] || {}),
      ambient,
    };
    setBasicLegend(null);
    setAmbient(ambient);
    setActiveLand(land_id);
  };

  const changeComparisonLegends = (land_id, legends) => {
    setComparisonLegendsState({
      land_id,
      items: legends || [],
    });
    setActiveLand(land_id);
  };

  const clearComparisonLegends = (land_id) => {
    setComparisonLegendsState((current) => {
      if (!current || (land_id && current.land_id !== land_id)) {
        return current;
      }

      return null;
    });
  };

  const value = {
    deleteAll, clearVisibleLegends,
    basicLegend, changeBasicLegend, deleteLegend, hideLegend, clearBasicLegend, recoverIfExists, recoverBasicLegend,
    enhanced, handleEnhanced, disableEnhanced,
    comparisonEnhanced, handleComparisonEnhanced, disableComparisonEnhanced,
    ambient, handleAmbient, hideAmbientLegend,
    comparisonLegends, changeComparisonLegends, clearComparisonLegends,

    activeLand
  };

  return (
    <>
      <MapLegendContext.Provider value={value}>
        {children}
      </MapLegendContext.Provider>
    </>
  );
};
